import { ChooseOption, ChooseOptionsQuestion, ChooseOptionsSpec } from 'types/aiChat';
import { isGlobalSearchEntityType } from 'types/globalSearch';

/**
 * The caps the server validates the tool's arguments against. Restated here because the wire is not
 * evidence: a payload outside them means the producer and this parser disagree about the contract,
 * and drawing a question with eleven choices or a hundred-and-fifty-one-character label would hide that.
 */
const MAX_QUESTIONS = 5;
const MAX_TOPIC_LENGTH = 40;
const MAX_QUESTION_LENGTH = 200;
const MAX_LABEL_LENGTH = 150;
const MAX_HINT_LENGTH = 120;
const MIN_OPTIONS = 2;
const MAX_OPTIONS = 10;

function parseBoundedText(value: unknown, maxLength: number): string | null {
    return typeof value === 'string' && value.trim().length > 0 && value.length <= maxLength ? value : null;
}

function parseOption(value: unknown): ChooseOption | null {
    if (!value || typeof value !== 'object') return null;
    const option = value as Record<string, unknown>;

    const label = parseBoundedText(option.label, MAX_LABEL_LENGTH);
    if (!label) return null;

    // A type this build does not know is dropped: the option then draws the initials tile.
    const typed =
        typeof option.entityType === 'string' && isGlobalSearchEntityType(option.entityType)
            ? { entityType: option.entityType }
            : {};

    // No hint arrives as an explicit null as readily as a missing key, so normalise before testing.
    const rawHint = option.hint ?? undefined;
    if (rawHint === undefined) return { label, ...typed };

    const hint = parseBoundedText(rawHint, MAX_HINT_LENGTH);
    return hint ? { label, hint, ...typed } : null;
}

/**
 * What a click sends. A choice travels back as plain text, so it has to carry whatever tells it apart:
 * full names genuinely collide — one tenant holds seven employees called "Märt Saarmets" — and two
 * label-only clicks would send the same sentence, leaving the model unable to tell which record was
 * picked. `label (hint)` is the literal shape the tool description and the system prompt promise the
 * model, parentheses included, so it is spelled here exactly and nowhere else.
 */
export function optionText({ label, hint }: ChooseOption) {
    return hint ? `${label} (${hint})` : label;
}

function parseQuestion(value: Record<string, unknown>): Omit<ChooseOptionsQuestion, 'topic'> | null {
    const question = parseBoundedText(value.question, MAX_QUESTION_LENGTH);
    if (!question) return null;

    if (!Array.isArray(value.options)) return null;
    if (value.options.length < MIN_OPTIONS || value.options.length > MAX_OPTIONS) return null;

    const options = value.options.map(parseOption);
    if (options.some((option) => !option)) return null;

    // Two choices sending the same text would leave the model unable to tell which one was picked.
    const parsedOptions = options as ChooseOption[];
    if (new Set(parsedOptions.map(optionText)).size !== parsedOptions.length) return null;

    return { question, options: parsedOptions };
}

function parseTopicQuestion(value: unknown): ChooseOptionsQuestion | null {
    if (!value || typeof value !== 'object') return null;
    const raw = value as Record<string, unknown>;

    const topic = parseBoundedText(raw.topic, MAX_TOPIC_LENGTH);
    const question = parseQuestion(raw);
    return topic && question ? { topic, ...question } : null;
}

/**
 * Re-validates the choose-options tool's output field by field. Returns null — never a partial
 * block — for anything outside the contract, and the caller shows an error in the block's place
 * rather than rendering choices the model did not author.
 *
 * A thread stored before questions came in blocks holds a single top-level `question`, still read
 * so that history renders as it did.
 */
export function parseChooseOptionsSpec(value: unknown): ChooseOptionsSpec | null {
    if (!value || typeof value !== 'object') return null;
    const spec = value as Record<string, unknown>;

    if (!('questions' in spec)) {
        const single = parseQuestion(spec);
        return single ? { questions: [single] } : null;
    }

    if (!Array.isArray(spec.questions)) return null;
    if (spec.questions.length < 1 || spec.questions.length > MAX_QUESTIONS) return null;

    const questions = spec.questions.map(parseTopicQuestion);
    if (questions.some((question) => !question)) return null;

    // Several answers travel back as one message, each behind its topic, so a shared topic would make two
    // answers indistinguishable.
    const topics = new Set(questions.map((question) => question?.topic?.trim().toLowerCase()));
    if (topics.size !== questions.length) return null;

    return { questions: questions as ChooseOptionsQuestion[] };
}
